import { useDeviceState } from '@hooks/device/useDeviceState';
import { useAuthentication } from '@hooks/interview/useAthentication';
import { useGetPath } from '@hooks/interview/useGetPath';
import { useInterviewAPI } from '@hooks/interview/useInterviewAPI';
import { useInterviewSession } from '@hooks/interview/useInterviewSession';
import { useMeetingSession } from '@hooks/meeting/useMeetingSession';
import { useChimeException } from '@hooks/utils/useChimeException';
import { useCondition } from '@hooks/utils/useCondition';
import { MeetingMode } from '@interfaces/chime.enum';
import { useMeetingManager } from '@providers/MeetingProvider';
import { externalApiUrl } from '@stores/atoms/url';
import { useSetAtom } from 'jotai';
import { useEffect, type FC } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { Container } from './style';

const IntervieweeSettingLayout: FC = () => {
  const navigate = useNavigate();
  const { rootPath } = useGetPath();
  const setExternalApiUrl = useSetAtom(externalApiUrl);
  const meetingManager = useMeetingManager();
  const { initialize } = useMeetingSession(meetingManager);
  const { authenticateUser } = useAuthentication();
  const { getInterviewInfo } = useInterviewSession();
  const { fetchJoinInterview } = useInterviewAPI();
  const { hasMeetingCondition, isUserLoggedIn } = useCondition();
  const { sendEvent } = useChimeException();

  const authenticateAgain = async () => {
    const { name, certificationCode, valuerSn, apiUrl } = getInterviewInfo();

    const externalApiAddr = `https://${apiUrl}`;
    setExternalApiUrl(externalApiAddr);

    await authenticateUser({
      name,
      certificationCode,
      apiUrl: externalApiAddr,
    });

    const interviewSession = await fetchJoinInterview({
      name,
      certificationCode,
      valuerSn,
    });

    await initialize({
      JoinInfo: interviewSession.data.JoinInfo,
      meetingMode: MeetingMode.Attendee,
    });
  };

  useEffect(() => {
    if (isUserLoggedIn) return;
    if (!hasMeetingCondition) {
      navigate(rootPath.HOME);
      return;
    }

    authenticateAgain();
  }, [isUserLoggedIn]);

  useEffect(() => {
    meetingManager.subscribeToEventDidReceive(sendEvent);
  }, []);

  useDeviceState();

  return (
    <Container>
      <Outlet />
    </Container>
  );
};

export default IntervieweeSettingLayout;
